import { useId, type ReactNode } from 'react';
import {
  Box,
  CloseButton,
  Divider,
  Heading,
  Icon,
  InlineStack,
  Link,
  List,
  ListItem,
  PlainModal,
  PlainModalDialog,
  PlainModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { PlumaModal } from '@customerio/pluma-components/css/components';
import { FlexMarkTile } from '@/components/flex-mark';
import * as styles from './purchase-sheet.css';

type PurchaseSheetProps = {
  title: string;
  titleId?: string;
  subline: ReactNode;
  rail: ReactNode;
  onClose: () => void;
  isCloseDisabled?: boolean;
  actions: ReactNode;
  children: ReactNode;
  [dataAttribute: `data-${string}`]: boolean | string | undefined;
};

export function PurchaseSheet({
  title,
  titleId,
  subline,
  rail,
  onClose,
  isCloseDisabled,
  actions,
  children,
  ...dataAttributes
}: PurchaseSheetProps) {
  return (
    <Box className={styles.container} data-testid="purchase-panel" {...dataAttributes}>
      <Box className={styles.sheet}>
        <Stack gap="300" className={styles.main}>
          <Box className={styles.close}>
            <CloseButton isDisabled={isCloseDisabled} onClick={onClose} />
          </Box>
          <Stack gap="100" alignInline="center" className={styles.header}>
            <FlexMarkTile size="lg" />
            <Heading level="2" size="lg" id={titleId}>
              {title}
            </Heading>
            <Text color="secondary">{subline}</Text>
          </Stack>
          {children}
          <Stack gap="100">{actions}</Stack>
        </Stack>
        <Box as="aside" className={styles.rail}>
          {rail}
        </Box>
      </Box>
    </Box>
  );
}

type PurchaseModalProps = Omit<PurchaseSheetProps, 'titleId'> & {
  isOpen: boolean;
  onCloseComplete?: () => void;
};

export function PurchaseModal({ isOpen, onCloseComplete, ...sheetProps }: PurchaseModalProps) {
  const titleId = useId();
  const canClose = sheetProps.isCloseDisabled !== true;

  return (
    <PlainModal
      isOpen={isOpen}
      onClose={sheetProps.onClose}
      onCloseComplete={onCloseComplete}
      shouldCloseOnOverlayClick={canClose}
      shouldCloseOnEscapePress={canClose}
    >
      <PlainModalOverlay className={PlumaModal.classNames.modalOverlay}>
        <PlainModalDialog className={styles.dialog} aria-labelledby={titleId}>
          <Box className={styles.dialogBody}>
            <PurchaseSheet titleId={titleId} {...sheetProps} />
          </Box>
        </PlainModalDialog>
      </PlainModalOverlay>
    </PlainModal>
  );
}

type PurchaseModalLoadingProps = {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
};

export function PurchaseModalLoading({ isOpen, onClose, onCloseComplete }: PurchaseModalLoadingProps) {
  return (
    <PlainModal isOpen={isOpen} onClose={onClose} onCloseComplete={onCloseComplete}>
      <PlainModalOverlay className={PlumaModal.classNames.modalOverlay}>
        <PlainModalDialog className={styles.loadingDialog} aria-label="Loading">
          <Spinner shouldShowLoadingText={true} />
        </PlainModalDialog>
      </PlainModalOverlay>
    </PlainModal>
  );
}

type PurchaseSheetRailProps = {
  onLearnMorePricing?: () => void;
  children: ReactNode;
};

export function PurchaseSheetRail({ onLearnMorePricing, children }: PurchaseSheetRailProps) {
  return (
    <Stack gap="200">
      {children}
      {onLearnMorePricing == null ? null : (
        <Stack gap="100">
          <Divider />
          <InlineStack gap="150" alignBlock="center" className={styles.learnMore}>
            <Icon name="info" size="sm" color="secondary" flexShrink="0" />
            <Link onClick={onLearnMorePricing}>Learn more about pricing</Link>
          </InlineStack>
        </Stack>
      )}
    </Stack>
  );
}

type PurchaseSheetRailSectionProps = {
  heading: string;
  items: readonly string[];
  [dataAttribute: `data-${string}`]: boolean | string | undefined;
};

export function PurchaseSheetRailSection({ heading, items, ...dataAttributes }: PurchaseSheetRailSectionProps) {
  return (
    <Stack gap="100" {...dataAttributes}>
      <Heading level="3" size="sm" className={styles.railHeading}>
        {heading}
      </Heading>
      <List styleType="none">
        {items.map((item) => (
          <ListItem key={item} className={styles.railItem}>
            <InlineStack gap="150" alignBlock="start">
              <Icon name="check" size="sm" color="primary" flexShrink="0" />
              <Text>{item}</Text>
            </InlineStack>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
